import { ArrowRight, Check } from "lucide-react";
import { Link, Navigate } from "react-router";
import { useProjectContext } from "@/app/project-context";
import { Button } from "@/components/ui/button";
import { brainPath } from "../brain-signpost";
import { WizardColumn } from "./wizard";

/**
 * `/projects/:projectId/setup/done` — where setup ends.
 *
 * Added 2026-10-03 at the owner's call, with the Documents step gone: setup
 * no longer takes files, so its last screen says the project is set up and
 * leads to the one place files are added, Brain → Sources. It used to end on
 * the overview, which said nothing about what to do next.
 *
 * A route of its own rather than a state of Review: activating the project
 * turns the wizard into edit mode, so Review re-renders as "Edit project
 * setup" under anything shown in its place. A route also survives a reload.
 *
 * Only an active project has finished setup. Any other is sent to the step
 * it reached, so this screen never claims a setup that has not happened.
 */
export function SetupDoneScreen() {
  const { project, projectId } = useProjectContext();

  if (project.status !== "active") {
    return <Navigate to={`/projects/${projectId}/setup`} replace />;
  }

  return (
    <WizardColumn>
      <section className="mt-6 flex flex-col items-center rounded-card-lg border-[1.5px] border-border bg-surface px-6 py-10 text-center shadow-flat">
        <span
          aria-hidden
          className="flex size-14 items-center justify-center rounded-full bg-ok-bg text-ok"
        >
          <Check size={28} strokeWidth={2.5} />
        </span>
        <div className="mt-5 font-display text-[0.7rem] font-semibold uppercase tracking-[0.1em] text-brand">
          {project.name}
        </div>
        <h1 className="mt-1 font-display text-[1.7rem] font-bold uppercase leading-[1.05] tracking-[0.01em] text-ink">
          Project successfully set up
        </h1>
        <p className="mt-3 max-w-[52ch] text-[0.95rem] leading-[1.5] text-ink-prose">
          Next, add the project’s files. The Brain answers questions from them.
        </p>
        <Button size="lg" asChild className="mt-7 hover:bg-brand-deep">
          <Link to={brainPath(projectId)}>
            Set up sources
            <ArrowRight size={18} aria-hidden />
          </Link>
        </Button>
        <Link
          to={`/projects/${projectId}/overview`}
          className="mt-4 text-[0.88rem] font-semibold text-muted underline-offset-2 hover:text-ink hover:underline"
        >
          Go to the project overview
        </Link>
      </section>
    </WizardColumn>
  );
}
